import { useCallback, useState, useEffect, useRef } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import {
  Account, useAppSelector, useAppDispatch, DriveRequest, ErrorStackItem, Message, getEnumKey, MessageHandlerDisplayType, useSendRequestMutation, DriveErrorMessages,
  DriveWatchedErrorMessages, CollectionFilesyncAPI, useGetGrants, PermissionSetType, useGetPermissions, GrantSetType, DirectoryInfo, GrantSet, getDirSymmKey, GenericNode,
  MaintainerGrantsMap, PermissionSetTypeMap, DirectoryInfoMap, DriveLimits, GenericListEntity, GenericEntryEntity, GenericNodesList, GenericEntry
} from "src/common";
import { uiActions, driveActions, DriveCallbackAsyncFunction } from "src/store";
import { RootState } from "src/store/configureStore";

export function useFetchStarred(current_account: Account) {
    const dispatch = useAppDispatch();
    const [sendRequest] = useSendRequestMutation();
    const default_permissions = useAppSelector((state: RootState) => state.drive.default_permissions);
    const default_grants = useAppSelector((state: RootState) => state.drive.default_grants);
    const [starred_list, setStarredList] = useState<GenericListEntity | undefined>();
    const [error, setError] = useState<boolean>(false);
    const { grants, error: grant_error, getGrants, destroyGrants } = useGetGrants(current_account);
    const { permissions, getPermissions, destroyPermissions } = useGetPermissions(current_account);

    // Description: hold working nodes
    const nodes_ref = useRef<GenericNode[]>([]);
    const working_node_ref = useRef<GenericNode>();
    const node_queue_ref = useRef<GenericEntryEntity[]>([]);

    // Description: Intermediate holds information while building the nodes
    const starred_list_info = useRef<GenericNodesList | undefined>();
    function setStarredListInfo(_starred_list_info?: GenericNodesList) {
        starred_list_info.current = _starred_list_info;
    }

    const maintainer_grants_ref = useRef<MaintainerGrantsMap>(new Map<string, GrantSetType>);
    function setMaintainerGrantsRef(id: string, maintainer_grants: GrantSetType) {
        maintainer_grants_ref.current.set(id, maintainer_grants);
    }
    const permission_sets_ref = useRef<PermissionSetTypeMap>(new Map<string, PermissionSetType>);
    function setPermissionSetsRef(id: string, permission_set: PermissionSetType) {
        permission_sets_ref.current.set(id, permission_set);
    }
    function getPermissionSetRef(collection_id: string): PermissionSetType | undefined {
        return permission_sets_ref.current.get(collection_id);
    }
    const directory_info_ref = useRef<DirectoryInfoMap>(new Map<string, DirectoryInfo>);
    function setDirectoryInfoRef(id: string, directory_info: DirectoryInfo) {
        directory_info_ref.current.set(id, directory_info);
    }

    // Description: Allow active permissions and default_permissions to be accessed
    const default_permissions_ref = useRef<PermissionSetType[]>(default_permissions);
    useEffect(() => {
        default_permissions_ref.current = default_permissions;
    }, [default_permissions]);

    // Description: Handle the name field in the starred node list
    useEffect(() => {
        if (!!permissions && !!working_node_ref.current) {
            setPermissionSetsRef(permissions.collection_id.B64(), permissions);
            destroyPermissions(); // NOTE: Return the permission state to undefined to be able to detect future changes
            handleStarredNodePermissions(working_node_ref.current, permissions);
        }
    }, [permissions]);

    // Description: useEffect hook for getting grant per row
    useEffect(() => {
        if (!!grants) {
            setMaintainerGrantsRef(grants.id, grants);
            if (!!working_node_ref.current) {
                const permissions_ref = getPermissionSetRef(working_node_ref.current.getCollectionId_asB64());
                !!permissions_ref ? handleStarredNodeGrants(working_node_ref.current, permissions_ref, grants) :
                    handleItemError(DriveErrorMessages.default, { stack_error: { data: working_node_ref.current.toObject() }, stack_message: DriveErrorMessages.error_sending_request });
            } else {
                handleItemError(DriveErrorMessages.default, { stack_error: { data: `missing working_node_ref on grant fetch: Collection_id: ${grants.collection_id}` }, stack_message: DriveErrorMessages.error_sending_request });
            };
        }

        if (!!grant_error) {
            !!working_node_ref.current ?
                handleItemError(DriveErrorMessages.default, { stack_error: { data: working_node_ref.current.toObject() }, stack_message: DriveErrorMessages.error_sending_request }) :
                handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_grant });
        };

    }, [grants, grant_error]);

    // Description: Fetch all starred items
    async function handleGetStarredNodes(page_index: number = 0) {
        const drive_request = await CollectionFilesyncAPI.getStarredItems(current_account, page_index, DriveLimits.DRIVE_PAGINATION_PAGE_SIZE, true);
        async function callback(message: FSMessage) {
            if (message.getStatus() === FSStatus.OK) {
                setStarredListInfo(new GenericNodesList(message));
                const _list = message.getNodesList();
                nodes_ref.current = _list.slice();
                getNext();
            } else {
                handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_message: DriveWatchedErrorMessages.error_fetching_watched_root_nodes }, message, MessageHandlerDisplayType.toastr);
            }
        }
        handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_fetching_watched_root_nodes);
    }

    // Description: Handle the name field in the watch node list
    async function handleStarredNodePermissions(node: GenericNode, _permissions: PermissionSetType) {
        const maintainer_id = node.getMaintainerId_asB64();
        if (!!maintainer_id) {
            // NOTE: Do a check for cached assets to bypass getting same collection again
            const _maintainer_grants = maintainer_grants_ref.current.get(maintainer_id);
            !!_maintainer_grants ? handleStarredNodeGrants(node, _permissions, _maintainer_grants) :
                getGrants(maintainer_id, node.getCollectionId_asB64(), default_permissions_ref.current, default_grants);
        } else {
            !!node.getParentId_asB64() ? fetchDirectoryInfo(node, _permissions) : handleDecryptName(node, _permissions);
        }
    }

    // Description: Handle the name field in the watch node list
    async function handleStarredNodeGrants(node: GenericNode, _permissions: PermissionSetType, _grants: GrantSetType) {
        const id = node.getId_asB64();
        const maintainer_id = node.getMaintainerId_asB64();
        // NOTE: If the node is missing the getWrappedDirKey get its parent (ie files uploaded through FS)
        if (!!maintainer_id && id !== maintainer_id) {
            fetchDirectoryInfo(node, _permissions, _grants);
        } else {
            handleDecryptName(node, _permissions, _grants);
            destroyGrants();
        }
    }

    // Description: Handle Parent information for those rows requiring parent symm key
    async function fetchDirectoryInfo(node: GenericNode, _permissions: PermissionSetType, _grants?: GrantSetType) {
        const parent_id = node.getParentId_asB64();
        // NOTE: Check on cached parent directory_info
        const _working_parent_dir = directory_info_ref.current.get(parent_id);
        if (!!_working_parent_dir) {
            handleDecryptName(node, _permissions, _grants, _working_parent_dir);
            return;
        }
        // NOTE: Only get this if _working_parent_dir is not saved
        const collection_id = node.getCollectionId_asB64();
        const request = await CollectionFilesyncAPI.getDirectoryInformationRequest(collection_id, parent_id);
        const drive_request = await DriveRequest.initializeRequest(request, current_account, _permissions);
        async function callback(message: FSMessage) {
            if (message.getStatus() === FSStatus.OK) {
                const dir = message.getDir();
                if (!!dir) {
                    const wrapped_dir_key = dir.getWrappedDirKey();
                    const key_version = dir.getWrappedDirKey()?.getKeyVersion() || 0;
                    const grant = !!_grants ? GrantSet.getGrantByRole(_grants, key_version) : undefined;
                    const reader_user_key = !!grant ? await grant.key(current_account) : undefined;
                    const symmetric_key = !!wrapped_dir_key ? await getDirSymmKey(wrapped_dir_key, current_account, _permissions, reader_user_key) : undefined;
                    if ((!!wrapped_dir_key && !!symmetric_key)) {
                        const working_parent_dir = {
                            symmetric_key,
                            wrapped_dir_key,
                            version: dir.getVersion_asB64(),
                            grants: _grants
                        };
                        setDirectoryInfoRef(parent_id, working_parent_dir);
                        handleDecryptName(node, _permissions, _grants, working_parent_dir);
                    } else {
                        handleItemError(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_maintainer_directory }, message);
                    }
                } else {
                    handleItemError(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_maintainer_directory }, message);
                }
            } else {
                handleItemError(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_maintainer_directory }, message);
            }
        }
        handleSendRequest(drive_request, callback, DriveErrorMessages.error_fetching_maintainer_directory);
    }

    // Description:  Handle entry name for all types and move to next item
    // NOTE: Handle the parent DirectoryInfo (symm key)
    async function handleDecryptName(node: GenericNode, _permissions: PermissionSetType, _grants?: GrantSetType, _directory_info?: DirectoryInfo) {
        const _entry = await GenericEntry.init(node, current_account, _permissions, _grants, _directory_info);
        if (!!_entry.last_modification_date) {
            node_queue_ref.current.push(_entry.item);
            getNext();
        } else {
            // NOTE: Fetch date_modified from getFullNodePath if missing in the root node
            handleGetPath(_entry, _permissions);
        }
        destroyGrants(); // NOTE: destroy grants to allow for a next iteration
    }


    // Description: Handle getting Path list and last modified date when missing
    async function handleGetPath(_entry: GenericEntry, _permissions: PermissionSetType) {
        const drive_request = await CollectionFilesyncAPI.getPath(current_account, _entry.collection_id, _entry.id, _permissions);
        async function callback(message: FSMessage) {
            const full_path_list = message.getFullNodePath();
            if (message.getStatus() === FSStatus.OK && !!full_path_list) {
                _entry.setFullPathList(full_path_list);
                node_queue_ref.current.push(_entry.item);
                getNext();
            } else {
                handleItemError(DriveErrorMessages.default, { stack_message: DriveWatchedErrorMessages.error_fetching_paths }, message);
            }
        }
        handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_fetching_paths);
    }

    // Description: Move to next item in queue
    function getNext() {
        working_node_ref.current = nodes_ref.current.shift(); // Use shift to start at the beginning of array
        !!working_node_ref.current ?
            getPermissions(working_node_ref.current.getCollectionId_asB64(), default_permissions) :
            handleSuccess();
    }

    // Description: Complete fetch method
    function handleSuccess() {
        if (!!starred_list_info.current) {
            starred_list_info.current.entries = node_queue_ref.current;
            setStarredList(starred_list_info.current.item);
            dispatch(driveActions.setPaginationInfo(starred_list_info.current.pagination_info));
        } else {
            handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_message: DriveWatchedErrorMessages.error_fetching_watched_root_nodes }, null, MessageHandlerDisplayType.toastr);
        }
    }

    // -----------------------------
    // HOOK General
    // -----------------------------
    // Description: Sends Request and handles error when the request object is null, pass it custom stack message
    function handleSendRequest<T>(request: DriveRequest | null, callback: DriveCallbackAsyncFunction<T>,
        stack_error: string = DriveErrorMessages.error_sending_request, user_message: string = DriveErrorMessages.default) {
        if (!!request) {
            request.setCallback(callback);
            sendRequest(request);
        } else {
            !!working_node_ref.current ?
                handleItemError(user_message, { stack_error: { data: working_node_ref.current.toObject() }, stack_message: DriveErrorMessages.error_sending_request }) :
                handlePageErrorMessage(user_message, { stack_error, stack_message: DriveErrorMessages.error_sending_request }, null, MessageHandlerDisplayType.toastr);
        }
    }

    // Description: Handle error message and send error to store
    function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null,
        display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
        const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
        const stack = new ErrorStackItem("[useFetchStarred Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
        dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
        setError(true);
    }

    // Description:  Handle queue item error
    function handleItemError(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null) {
        const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
        const stack = new ErrorStackItem("[useFetchStarred Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), stack));
        getNext();
    }

    // Description: reset hook 
    function reset() {
        nodes_ref.current = [];
        node_queue_ref.current = [];
        working_node_ref.current = undefined;
        setStarredListInfo(undefined);
        setError(false);
        destroyGrants();
        destroyPermissions();
    }

    // Callbacks
    // -----------------------------
    // Description: Callback for fetching starred items
    const fetchStarredList = useCallback((page: number = 1) => {
        reset();
        const page_index = Number(page - 1);
        handleGetStarredNodes(page_index);
    }, []);

    const resetStarredList = useCallback(() => {
        reset();
    }, []);

    return {
        fetchStarredList,
        resetStarredList,
        starred_list,
        error
    };
}
